/**
 * Generato automaticamente.
 * framework-cmp-domande
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  GetFrontPagamentiIdEbolloCallbackIndietro200,
  GetFrontPagamentiIdEbolloCallbackIndietroParams,
  GetFrontPagamentiParams,
  PagamentoDTO,
  PagamentoWithDettaglioDTO,
  SchedulationResult,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Chiamata batch che uno scheduler può effettuare per settare lo stato 'FALLIMENTO' di pagamenti e-bollo con esito pendente da più di {maxNumPendingDays} giorni
 * @summary Chiamata batch che uno scheduler può effettuare per avviare lo start-retry verso il sistema di pagamento per ottenere le notifiche di pagamenti non inviate o fallite
 */
export const postFrontPagamentiStartRetryNotifichePagamenti = (
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<SchedulationResult>(
    {
      url: `/framework-cmp-domande/pagamenti/start-retry-notifiche-pagamenti`,
      method: 'post',
    },
    options,
  );
};

export const getPostFrontPagamentiStartRetryNotifichePagamentiMutationOptions =
  <
    TError = ErrorType<unknown>,
    TVariables = void,
    TContext = unknown,
  >(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<typeof postFrontPagamentiStartRetryNotifichePagamenti>
      >,
      TError,
      TVariables,
      TContext
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  }): UseMutationOptions<
    Awaited<ReturnType<typeof postFrontPagamentiStartRetryNotifichePagamenti>>,
    TError,
    TVariables,
    TContext
  > => {
    const { mutation: mutationOptions, request: requestOptions } =
      options ?? {};

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<typeof postFrontPagamentiStartRetryNotifichePagamenti>
      >,
      TVariables
    > = () => {
      return postFrontPagamentiStartRetryNotifichePagamenti(requestOptions);
    };

    return { mutationFn, ...mutationOptions };
  };

export type PostFrontPagamentiStartRetryNotifichePagamentiMutationResult =
  NonNullable<
    Awaited<ReturnType<typeof postFrontPagamentiStartRetryNotifichePagamenti>>
  >;

export type PostFrontPagamentiStartRetryNotifichePagamentiMutationError =
  ErrorType<unknown>;

/**
 * @summary Chiamata batch che uno scheduler può effettuare per avviare lo start-retry verso il sistema di pagamento per ottenere le notifiche di pagamenti non inviate o fallite
 */
export const usePostFrontPagamentiStartRetryNotifichePagamenti = <
  TError = ErrorType<unknown>,
  TVariables = void,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontPagamentiStartRetryNotifichePagamenti>>,
    TError,
    TVariables,
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostFrontPagamentiStartRetryNotifichePagamentiMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Chiamata batch che uno scheduler può effettuare per settare lo stato 'FALLIMENTO' di transazioni di pagamenti con esito PENDENTE da più di {maxNumPendingMinutes} minuti
 * @summary Chiamata batch che uno scheduler può effettuare per settare lo stato 'FALLIMENTO' di transazioni di pagamenti con esito PENDENTE da più di {maxNumPendingMinutes} minuti
 */
export const postFrontPagamentiRimuoviTransazioniPagamentiPendentiMaxNumPendingMinutes =
  (
    maxNumPendingMinutes: number,
    options?: SecondParameter<typeof apiClientRequest>,
  ) => {
    return apiClientRequest<SchedulationResult>(
      {
        url: `/framework-cmp-domande/pagamenti/rimuovi-transazioni-pagamenti-pendenti/${maxNumPendingMinutes}`,
        method: 'post',
      },
      options,
    );
  };

export const getPostFrontPagamentiRimuoviTransazioniPagamentiPendentiMaxNumPendingMinutesMutationOptions =
  <TError = ErrorType<unknown>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<
          typeof postFrontPagamentiRimuoviTransazioniPagamentiPendentiMaxNumPendingMinutes
        >
      >,
      TError,
      { maxNumPendingMinutes: number },
      TContext
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  }): UseMutationOptions<
    Awaited<
      ReturnType<
        typeof postFrontPagamentiRimuoviTransazioniPagamentiPendentiMaxNumPendingMinutes
      >
    >,
    TError,
    { maxNumPendingMinutes: number },
    TContext
  > => {
    const { mutation: mutationOptions, request: requestOptions } =
      options ?? {};

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<
          typeof postFrontPagamentiRimuoviTransazioniPagamentiPendentiMaxNumPendingMinutes
        >
      >,
      { maxNumPendingMinutes: number }
    > = props => {
      const { maxNumPendingMinutes } = props ?? {};

      return postFrontPagamentiRimuoviTransazioniPagamentiPendentiMaxNumPendingMinutes(
        maxNumPendingMinutes,
        requestOptions,
      );
    };

    return { mutationFn, ...mutationOptions };
  };

export type PostFrontPagamentiRimuoviTransazioniPagamentiPendentiMaxNumPendingMinutesMutationResult =
  NonNullable<
    Awaited<
      ReturnType<
        typeof postFrontPagamentiRimuoviTransazioniPagamentiPendentiMaxNumPendingMinutes
      >
    >
  >;

export type PostFrontPagamentiRimuoviTransazioniPagamentiPendentiMaxNumPendingMinutesMutationError =
  ErrorType<unknown>;

/**
 * @summary Chiamata batch che uno scheduler può effettuare per settare lo stato 'FALLIMENTO' di transazioni di pagamenti con esito PENDENTE da più di {maxNumPendingMinutes} minuti
 */
export const usePostFrontPagamentiRimuoviTransazioniPagamentiPendentiMaxNumPendingMinutes =
  <TError = ErrorType<unknown>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<
          typeof postFrontPagamentiRimuoviTransazioniPagamentiPendentiMaxNumPendingMinutes
        >
      >,
      TError,
      { maxNumPendingMinutes: number },
      TContext
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  }) => {
    const mutationOptions =
      getPostFrontPagamentiRimuoviTransazioniPagamentiPendentiMaxNumPendingMinutesMutationOptions(
        options,
      );

    return useMutation(mutationOptions);
  };
/**
 * Chiamata batch che uno scheduler può effettuare per settare lo stato 'FALLIMENTO' di pagamenti e-bollo con esito pendente da più di {maxNumPendingDays} giorni
 * @summary Chiamata batch che uno scheduler può effettuare per settare lo stato 'FALLIMENTO' di pagamenti e-bollo con esito pendente da più di {maxNumPendingDays} giorni
 */
export const postFrontPagamentiRimuoviPagamentiEbolloPendentiMaxNumPendingDays =
  (
    maxNumPendingDays: number,
    options?: SecondParameter<typeof apiClientRequest>,
  ) => {
    return apiClientRequest<SchedulationResult>(
      {
        url: `/framework-cmp-domande/pagamenti/rimuovi-pagamenti-ebollo-pendenti/${maxNumPendingDays}`,
        method: 'post',
      },
      options,
    );
  };

export const getPostFrontPagamentiRimuoviPagamentiEbolloPendentiMaxNumPendingDaysMutationOptions =
  <TError = ErrorType<unknown>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<
          typeof postFrontPagamentiRimuoviPagamentiEbolloPendentiMaxNumPendingDays
        >
      >,
      TError,
      { maxNumPendingDays: number },
      TContext
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  }): UseMutationOptions<
    Awaited<
      ReturnType<
        typeof postFrontPagamentiRimuoviPagamentiEbolloPendentiMaxNumPendingDays
      >
    >,
    TError,
    { maxNumPendingDays: number },
    TContext
  > => {
    const { mutation: mutationOptions, request: requestOptions } =
      options ?? {};

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<
          typeof postFrontPagamentiRimuoviPagamentiEbolloPendentiMaxNumPendingDays
        >
      >,
      { maxNumPendingDays: number }
    > = props => {
      const { maxNumPendingDays } = props ?? {};

      return postFrontPagamentiRimuoviPagamentiEbolloPendentiMaxNumPendingDays(
        maxNumPendingDays,
        requestOptions,
      );
    };

    return { mutationFn, ...mutationOptions };
  };

export type PostFrontPagamentiRimuoviPagamentiEbolloPendentiMaxNumPendingDaysMutationResult =
  NonNullable<
    Awaited<
      ReturnType<
        typeof postFrontPagamentiRimuoviPagamentiEbolloPendentiMaxNumPendingDays
      >
    >
  >;

export type PostFrontPagamentiRimuoviPagamentiEbolloPendentiMaxNumPendingDaysMutationError =
  ErrorType<unknown>;

/**
 * @summary Chiamata batch che uno scheduler può effettuare per settare lo stato 'FALLIMENTO' di pagamenti e-bollo con esito pendente da più di {maxNumPendingDays} giorni
 */
export const usePostFrontPagamentiRimuoviPagamentiEbolloPendentiMaxNumPendingDays =
  <TError = ErrorType<unknown>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<
          typeof postFrontPagamentiRimuoviPagamentiEbolloPendentiMaxNumPendingDays
        >
      >,
      TError,
      { maxNumPendingDays: number },
      TContext
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  }) => {
    const mutationOptions =
      getPostFrontPagamentiRimuoviPagamentiEbolloPendentiMaxNumPendingDaysMutationOptions(
        options,
      );

    return useMutation(mutationOptions);
  };
/**
 * Cerca una serie di pagamenti da una lista di id.
 * @summary Cerca una serie di pagamenti da una lista di id.
 */
export const getFrontPagamenti = (
  params?: GetFrontPagamentiParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<PagamentoDTO[]>(
    { url: `/framework-cmp-domande/pagamenti`, method: 'get', params, signal },
    options,
  );
};

export const getGetFrontPagamentiQueryKey = (
  params?: GetFrontPagamentiParams,
) => [`/framework-cmp-domande/pagamenti`, ...(params ? [params] : [])] as const;

export const getGetFrontPagamentiQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontPagamenti>>,
  TError = ErrorType<unknown>,
>(
  params?: GetFrontPagamentiParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontPagamenti>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontPagamenti>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetFrontPagamentiQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontPagamenti>>
  > = ({ signal }) => getFrontPagamenti(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetFrontPagamentiQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontPagamenti>>
>;
export type GetFrontPagamentiQueryError = ErrorType<unknown>;

/**
 * @summary Cerca una serie di pagamenti da una lista di id.
 */
export const useGetFrontPagamenti = <
  TData = Awaited<ReturnType<typeof getFrontPagamenti>>,
  TError = ErrorType<unknown>,
>(
  params?: GetFrontPagamentiParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontPagamenti>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontPagamentiQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Recupera il dettaglio del pagamento
 * @summary dettaglio del pagamento
 */
export const getFrontPagamentiId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<PagamentoWithDettaglioDTO>(
    { url: `/framework-cmp-domande/pagamenti/${id}`, method: 'get', signal },
    options,
  );
};

export const getGetFrontPagamentiIdQueryKey = (id: string) =>
  [`/framework-cmp-domande/pagamenti/${id}`] as const;

export const getGetFrontPagamentiIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontPagamentiId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontPagamentiId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontPagamentiId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetFrontPagamentiIdQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontPagamentiId>>
  > = ({ signal }) => getFrontPagamentiId(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetFrontPagamentiIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontPagamentiId>>
>;
export type GetFrontPagamentiIdQueryError = ErrorType<unknown>;

/**
 * @summary dettaglio del pagamento
 */
export const useGetFrontPagamentiId = <
  TData = Awaited<ReturnType<typeof getFrontPagamentiId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontPagamentiId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontPagamentiIdQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Callback che accoglie una richiesta di ritorno al Portale (senza completare il pagamento) da e-bollo
 * @summary Callback che accoglie una richiesta di ritorno al Portale (senza completare il pagamento) da e-bollo
 */
export const getFrontPagamentiIdEbolloCallbackIndietro = (
  idTransazione: string,
  params: GetFrontPagamentiIdEbolloCallbackIndietroParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<GetFrontPagamentiIdEbolloCallbackIndietro200>(
    {
      url: `/framework-cmp-domande/pagamenti/${idTransazione}/ebollo-callback-indietro`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetFrontPagamentiIdEbolloCallbackIndietroQueryKey = (
  idTransazione: string,
  params: GetFrontPagamentiIdEbolloCallbackIndietroParams,
) =>
  [
    `/framework-cmp-domande/pagamenti/${idTransazione}/ebollo-callback-indietro`,
    ...(params ? [params] : []),
  ] as const;

export const getGetFrontPagamentiIdEbolloCallbackIndietroQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontPagamentiIdEbolloCallbackIndietro>>,
  TError = ErrorType<unknown>,
>(
  idTransazione: string,
  params: GetFrontPagamentiIdEbolloCallbackIndietroParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontPagamentiIdEbolloCallbackIndietro>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontPagamentiIdEbolloCallbackIndietro>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFrontPagamentiIdEbolloCallbackIndietroQueryKey(idTransazione, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontPagamentiIdEbolloCallbackIndietro>>
  > = ({ signal }) =>
    getFrontPagamentiIdEbolloCallbackIndietro(
      idTransazione,
      params,
      requestOptions,
      signal,
    );

  return { queryKey, queryFn, enabled: !!idTransazione, ...queryOptions };
};

export type GetFrontPagamentiIdEbolloCallbackIndietroQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontPagamentiIdEbolloCallbackIndietro>>
>;
export type GetFrontPagamentiIdEbolloCallbackIndietroQueryError =
  ErrorType<unknown>;

/**
 * @summary Callback che accoglie una richiesta di ritorno al Portale (senza completare il pagamento) da e-bollo
 */
export const useGetFrontPagamentiIdEbolloCallbackIndietro = <
  TData = Awaited<ReturnType<typeof getFrontPagamentiIdEbolloCallbackIndietro>>,
  TError = ErrorType<unknown>,
>(
  idTransazione: string,
  params: GetFrontPagamentiIdEbolloCallbackIndietroParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontPagamentiIdEbolloCallbackIndietro>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontPagamentiIdEbolloCallbackIndietroQueryOptions(
    idTransazione,
    params,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
